/* GW Collection Note — <gw-collection-note>
   The expand/collapse note for sections/gw-collection-note.liquid.

   TWO JOBS, both of which exist because the copy is ONE richtext field.

   1. HOIST THE HEADING. Figma (112:164) draws the 20px heading OUTSIDE the
      clamped 61px body box, but a richtext is a single HTML blob and Liquid
      cannot split it. The first h1-h6 among the body's own children is moved
      into [data-gw-cn-heading], so the title is never truncated and never
      counts against the line clamp.

   2. DECIDE WHETHER A TOGGLE IS WARRANTED. The button ships `hidden` and is
      revealed only when the collapsed box actually overflows. Short copy gets
      no button at all rather than a control that expands nothing.

   NO-JS BEHAVIOUR IS THE POINT OF data-js. The stylesheet clamps only inside
   [data-js], so a visitor without JS sees the full text and no button, instead
   of truncated copy behind a dead control. Same call as the data-js gating
   recorded on gw-collection-grid.

   Markup contract:
     <gw-collection-note>
       <div class="__text">
         <div data-gw-cn-heading hidden></div>   (required, empty)
         <div data-gw-cn-body>{{ richtext }}</div> (required)
       </div>
       <button data-gw-cn-toggle aria-expanded aria-controls hidden>
         <span data-gw-cn-more>  <span data-gw-cn-less hidden>
       </button>
     </gw-collection-note>                                                   */

class GwCollectionNote extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;

    this.body = this.querySelector('[data-gw-cn-body]');
    this.headingSlot = this.querySelector('[data-gw-cn-heading]');
    this.toggle = this.querySelector('[data-gw-cn-toggle]');
    if (!this.body || !this.toggle) return;

    this.bound = true;
    this.hoistHeading();

    /* Clamping starts only now, so nothing is ever hidden before the code that
       can reveal it is running. */
    this.setAttribute('data-js', '');
    this.setAttribute('data-expanded', 'false');

    this.toggle.addEventListener('click', () => this.setExpanded(this.toggle.getAttribute('aria-expanded') !== 'true'));

    this.measure();

    /* The clamp is 3 lines above 1025 and 1 below, so the overflow verdict can
       flip on resize. Fonts land after connect and reflow the body, so the same
       re-measure covers that too. */
    this.onResize = this.debounce(() => this.measure(), 150);
    window.addEventListener('resize', this.onResize);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.measure());
  }

  disconnectedCallback() {
    if (this.onResize) window.removeEventListener('resize', this.onResize);
  }

  /* Only a heading that is the body's OWN child is hoisted, and only the first
     one — a h3 buried inside the copy is part of the copy. A metafield-sourced
     richtext arrives wrapped in one <div class="metafield-rich_text_field">, so
     a lone wrapper div is looked through; otherwise the clamp counts the
     heading as one of its lines. */
  hoistHeading() {
    if (!this.headingSlot) return;
    let root = this.body;
    if (root.children.length === 1 && root.firstElementChild.tagName === 'DIV') root = root.firstElementChild;
    const heading = Array.from(root.children).find((el) => /^H[1-6]$/.test(el.tagName));
    if (!heading) return;

    this.headingSlot.appendChild(heading);
    this.headingSlot.hidden = false;
  }

  /* scrollHeight vs clientHeight is the only reliable read of a -webkit-line-clamp
     overflow; the clamp leaves no other signal. It is valid only while collapsed,
     so an expanded note is collapsed for the duration of the read. */
  measure() {
    const wasExpanded = this.toggle.getAttribute('aria-expanded') === 'true';
    if (wasExpanded) this.setAttribute('data-expanded', 'false');

    const overflows = this.body.scrollHeight - this.body.clientHeight > 1;

    if (wasExpanded) this.setAttribute('data-expanded', 'true');

    this.toggle.hidden = !overflows;
    if (!overflows && wasExpanded) this.setExpanded(false);
  }

  setExpanded(expanded) {
    this.toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    this.setAttribute('data-expanded', expanded ? 'true' : 'false');

    const more = this.querySelector('[data-gw-cn-more]');
    const less = this.querySelector('[data-gw-cn-less]');
    if (more) more.hidden = expanded;
    if (less) less.hidden = !expanded;
  }

  debounce(fn, wait) {
    let t;
    return () => {
      clearTimeout(t);
      t = setTimeout(fn, wait);
    };
  }
}

if (!customElements.get('gw-collection-note')) {
  customElements.define('gw-collection-note', GwCollectionNote);
}
